import type { FieldDefinition } from "../fields/types.js";

/**
 * How a page's fields are arranged into rows and columns — kept apart from the component so it can
 * be tested as the plain data transformation it is.
 *
 * `position`, `rowGroup` and `widthSpan` ride on the field row rather than being declared on
 * `FieldDefinition`, which is the validation-facing shape. Reading them through these accessors
 * keeps the casting in one place.
 */

export const DEFAULT_WIDTH_SPAN = 12;

export const positionOf = (field: FieldDefinition): number =>
  (field as { position?: number }).position ?? 0;

export const rowGroupOf = (field: FieldDefinition): number =>
  (field as { rowGroup?: number }).rowGroup ?? 0;

export const widthSpanOf = (field: FieldDefinition): number =>
  (field as { widthSpan?: number }).widthSpan ?? DEFAULT_WIDTH_SPAN;

/**
 * Groups fields into rows: `rowGroup` 0 means "own row", and any other value joins *consecutive*
 * fields carrying the same value.
 *
 * Input is sorted by `position` first. That sort used to read
 * `a.position ?? 0 - (b.position ?? 0)`, which `??` binding looser than `-` parses as
 * `a.position ?? (0 - b.position)` — so the comparator returned `a`'s position and never looked at
 * `b` at all. It is not a valid comparator, and the order it produced was whatever the engine's
 * sort happened to do with it.
 */
export const groupIntoRows = (fields: readonly FieldDefinition[]): FieldDefinition[][] => {
  const ordered = fields.slice().sort((a, b) => positionOf(a) - positionOf(b));

  const rows: FieldDefinition[][] = [];
  let currentGroup: number | null = null;

  for (const field of ordered) {
    const group = rowGroupOf(field);
    if (group !== 0 && group === currentGroup && rows.length > 0) {
      rows[rows.length - 1]!.push(field);
    } else {
      rows.push([field]);
      currentGroup = group;
    }
  }

  return rows;
};

/**
 * How wide each field in a row should be, on a 12-column grid.
 *
 * `widthSpan` defaults to 12 in the schema, so a row whose fields have all been left alone carries
 * no width intent at all — honouring those literally would stack every side-by-side pair back into
 * a single column. Such a row is divided evenly instead, which is what putting two fields on one
 * row plainly means. Once any field in the row has a width set, the declared widths are used as
 * given.
 */
export const spansFor = (row: readonly FieldDefinition[]): number[] => {
  const declared = row.map(widthSpanOf);
  if (declared.every((span) => span === DEFAULT_WIDTH_SPAN)) {
    const share = Math.max(1, Math.floor(DEFAULT_WIDTH_SPAN / row.length));
    return row.map(() => share);
  }
  return declared;
};

/**
 * Tailwind only emits classes it can see written out, so the twelve spans are spelled literally —
 * `@md:col-span-${n}` would compile to nothing at all.
 *
 * Every cell is `col-span-12` (full width) until the *container* reaches `@md` (28rem). A container
 * query, not a viewport one: the same renderer draws the applicant's page, where that box is
 * effectively the viewport, and the builder's preview pane, where it is a simulated device width no
 * viewport query can see.
 */
const SPAN_CLASS: Readonly<Record<number, string>> = {
  1: "@md:col-span-1",
  2: "@md:col-span-2",
  3: "@md:col-span-3",
  4: "@md:col-span-4",
  5: "@md:col-span-5",
  6: "@md:col-span-6",
  7: "@md:col-span-7",
  8: "@md:col-span-8",
  9: "@md:col-span-9",
  10: "@md:col-span-10",
  11: "@md:col-span-11",
  12: "@md:col-span-12",
};

export const spanClassFor = (span: number): string =>
  SPAN_CLASS[span] ?? SPAN_CLASS[DEFAULT_WIDTH_SPAN]!;
